import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  FileText,
  Loader2,
  Target,
  Upload,
  X,
} from 'lucide-react';
import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { analyzeATSCompliance } from '../../../utils/atsComplianceAnalyzer';
import { extractResumeText } from '../../../utils/extractResumeText';
import { analyzeGaps, extractJDStructure } from '../../../utils/jdAnalysis';
import { analyzeJobMatch } from '../../../utils/jobMatchingAlgorithm';
import { trackCTA } from '../../../utils/metaPixel';

/* ── Types ── */
type AnalyzerTab = 'jd-match';

interface AnalysisResult {
  score: number;
  label: string;
  strengths: string[];
  gaps: string[];
  recommendations: string[];
}

interface HomeInstantAnalyzerProps {
  onNavigate?: (view: string) => void;
  /** embedded = inside funnel hero (no section title); section = standalone band */
  variant?: 'embedded' | 'section';
}

/* ── Animated score ring ── */
function ScoreRing({ score, size = 120 }: { score: number; size?: number }) {
  const strokeWidth = 8;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;

  const color =
    score >= 80
      ? 'var(--ds-marketing-accent, #22c55e)'
      : score >= 60
        ? '#f59e0b'
        : '#ef4444';

  return (
    <div className="instant-analyzer__ring" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="var(--ds-border, #e2e8f0)"
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: 'stroke-dashoffset 1s ease-out' }}
        />
      </svg>
      <div className="instant-analyzer__ring-value">
        <span className="instant-analyzer__ring-number" style={{ color }}>
          {score}
        </span>
        <span className="instant-analyzer__ring-label">/100</span>
      </div>
    </div>
  );
}

/* ── Main component ── */
const HomeInstantAnalyzer: React.FC<HomeInstantAnalyzerProps> = ({
  onNavigate,
  variant = 'section',
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [resumeText, setResumeText] = useState('');
  const [jdText, setJdText] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState('');

  const handleFile = useCallback(async (file: File) => {
    setError('');
    setResult(null);

    const validTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword',
    ];
    if (!validTypes.includes(file.type)) {
      setError(t('home.analyzer.errorFormat'));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError(t('home.analyzer.errorSize'));
      return;
    }

    setResumeFile(file);

    try {
      const text = await extractResumeText(file);
      if (!text || text.trim().length < 50) {
        setError(t('home.analyzer.errorParse'));
        setResumeFile(null);
        return;
      }
      setResumeText(text);
    } catch {
      setError(t('home.analyzer.errorParse'));
      setResumeFile(null);
    }
  }, [t]);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      const file = e.dataTransfer.files?.[0];
      if (file) handleFile(file);
    },
    [handleFile],
  );

  const handleAnalyze = useCallback(async () => {
    if (!resumeText) return;
    setAnalyzing(true);
    setError('');
    trackCTA('instant_analyzer_analyze');

    try {
      // Small delay for perceived quality
      await new Promise((r) => setTimeout(r, 600));

      const hasJd = jdText.trim().length > 30;

      if (hasJd) {
        // JD Match mode — use both analyzers
        const jobData = {
          title: '',
          company: '',
          description: jdText,
          skills: [] as string[],
        };
        const jdStructure = extractJDStructure(jobData);

        // Extract skills from JD structure for the matching algorithm
        const resumeProfile = {
          skills: extractSkillsFromText(resumeText),
          experience: resumeText,
          education: resumeText,
          keywords: [],
        };
        const jobProfile = {
          requiredSkills: jdStructure.requiredSkills,
          preferredSkills: jdStructure.preferredSkills,
          requirements: jdStructure.responsibilities,
          description: jdText,
        };

        const matchResult = analyzeJobMatch(resumeProfile, jobProfile);
        const gapResult = analyzeGaps(resumeText, jdStructure);

        const strengths = gapResult.strongMatches
          .slice(0, 6)
          .map((s) => s.skill);
        const missing = gapResult.missingSkills
          .slice(0, 6)
          .map((s) => s.skill);

        const recs: string[] = [];
        if (missing.length > 0) {
          recs.push(
            t('home.analyzer.recAddSkills', {
              skills: missing.slice(0, 3).join(', '),
            }),
          );
        }
        if (matchResult.factors.keywordMatch < 50) {
          recs.push(t('home.analyzer.recKeywords'));
        }
        if (matchResult.factors.experienceMatch < 60) {
          recs.push(t('home.analyzer.recExperience'));
        }

        setResult({
          score: matchResult.overallScore,
          label: t('home.analyzer.matchLabel'),
          strengths,
          gaps: missing,
          recommendations: recs,
        });
      } else {
        // ATS-only mode (no JD provided)
        const atsReport = analyzeATSCompliance(resumeText, {
          skills: [],
          description: '',
        });

        setResult({
          score: atsReport.overallScore,
          label: t('home.analyzer.atsLabel'),
          strengths: atsReport.strengths.map((s) => s.replace(/^[✓✗⚠️✅❌ΓÇó•]+\s*/g, '')),
          gaps: atsReport.warnings.map((w) => w.replace(/^[✓✗⚠️✅❌ΓÇó•]+\s*/g, '')),
          recommendations: atsReport.recommendations.slice(0, 3),
        });
      }
    } catch {
      setError(t('home.analyzer.errorGeneric'));
    } finally {
      setAnalyzing(false);
    }
  }, [resumeText, jdText, t]);

  const clearResume = () => {
    setResumeFile(null);
    setResumeText('');
    setResult(null);
    setError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSignup = () => {
    trackCTA('instant_analyzer_signup');
    onNavigate?.('signup');
  };

  const canAnalyze = resumeText.length > 0;
  const hasJd = jdText.trim().length > 30;

  const isEmbedded = variant === 'embedded';

  return (
    <section
      id={isEmbedded ? undefined : 'instant-analyzer'}
      className={`instant-analyzer-section scroll-mt-24${isEmbedded ? ' instant-analyzer-section--embedded' : ''}`}
      aria-label={isEmbedded ? t('home.analyzer.title') : undefined}
    >
      <div className="instant-analyzer__container">
        {!isEmbedded ? (
          <div className="instant-analyzer__header">
            <h2 className="instant-analyzer__title">
              <Target
                className="instant-analyzer__title-icon"
                aria-hidden
              />
              {t('home.analyzer.title')}
            </h2>
            <p className="instant-analyzer__subtitle">
              {t('home.analyzer.subtitle')}
            </p>
          </div>
        ) : null}

        {/* ── Input area ── */}
        <div className="instant-analyzer__body">
          <div className="instant-analyzer__inputs">
            {/* Resume upload */}
            <div className="instant-analyzer__input-group">
              <label className="instant-analyzer__field-label">
                {t('home.analyzer.resumeLabel')}
              </label>
              {!resumeFile ? (
                <div
                  className={`instant-analyzer__dropzone ${dragOver ? 'instant-analyzer__dropzone--active' : ''}`}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragOver(true);
                  }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click();
                  }}
                >
                  <Upload className="instant-analyzer__dropzone-icon" aria-hidden />
                  <span className="instant-analyzer__dropzone-text">
                    {t('home.analyzer.dropzoneText')}
                  </span>
                  <span className="instant-analyzer__dropzone-hint">
                    {t('home.analyzer.dropzoneHint')}
                  </span>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx"
                    className="sr-only"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleFile(file);
                    }}
                  />
                </div>
              ) : (
                <div className="instant-analyzer__file-badge">
                  <FileText className="w-4 h-4 shrink-0" style={{ color: 'var(--ds-marketing-accent, #5F8568)' }} />
                  <span className="instant-analyzer__file-name">{resumeFile.name}</span>
                  <button
                    type="button"
                    onClick={clearResume}
                    className="instant-analyzer__file-remove touch-manipulation"
                    aria-label="Remove file"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </div>

            {/* Job description textarea */}
            <div className="instant-analyzer__input-group">
              <label className="instant-analyzer__field-label">
                {t('home.analyzer.jdLabel')}
                <span className="instant-analyzer__field-optional">
                  {t('home.analyzer.jdOptional')}
                </span>
              </label>
              <textarea
                className="instant-analyzer__textarea"
                rows={5}
                placeholder={t('home.analyzer.jdPlaceholder')}
                value={jdText}
                onChange={(e) => setJdText(e.target.value)}
              />
              {hasJd && (
                <span className="instant-analyzer__jd-hint">
                  {t('home.analyzer.jdActive')}
                </span>
              )}
            </div>

            {/* Error */}
            {error && (
              <div className="instant-analyzer__error" role="alert">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                {error}
              </div>
            )}

            {/* Analyze button */}
            <button
              type="button"
              onClick={handleAnalyze}
              disabled={!canAnalyze || analyzing}
              className="home-marketing-cta instant-analyzer__cta group font-sans touch-manipulation"
            >
              {analyzing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t('home.analyzer.analyzing')}
                </>
              ) : hasJd ? (
                <>
                  {t('home.analyzer.analyzeMatch')}
                  <ArrowRight className="h-4 w-4 shrink-0 transition-transform duration-200 group-hover:translate-x-0.5" aria-hidden />
                </>
              ) : (
                <>
                  {t('home.analyzer.analyzeAts')}
                  <ArrowRight className="h-4 w-4 shrink-0 transition-transform duration-200 group-hover:translate-x-0.5" aria-hidden />
                </>
              )}
            </button>
          </div>

          {/* ── Results ── */}
          {result && (
            <div className="instant-analyzer__results">
              {/* Score ring */}
              <div className="instant-analyzer__score-section">
                <ScoreRing score={result.score} />
                <span className="instant-analyzer__score-label">{result.label}</span>
              </div>

              {/* Strengths */}
              {result.strengths.length > 0 && (
                <div className="instant-analyzer__bucket">
                  <h3 className="instant-analyzer__bucket-title instant-analyzer__bucket-title--ok">
                    {t('home.analyzer.strongMatches')}
                  </h3>
                  <ul className="instant-analyzer__bucket-list">
                    {result.strengths.map((s, i) => (
                      <li key={i} className="instant-analyzer__bucket-item">
                        <CheckCircle2 className="w-4 h-4 shrink-0" style={{ color: '#22c55e' }} />
                        {s}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Gaps */}
              {result.gaps.length > 0 && (
                <div className="instant-analyzer__bucket">
                  <h3 className="instant-analyzer__bucket-title instant-analyzer__bucket-title--warn">
                    {t('home.analyzer.missing')}
                  </h3>
                  <ul className="instant-analyzer__bucket-list">
                    {result.gaps.map((g, i) => (
                      <li key={i} className="instant-analyzer__bucket-item">
                        <AlertTriangle className="w-4 h-4 shrink-0" style={{ color: '#f59e0b' }} />
                        {g}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Recommendations */}
              {result.recommendations.length > 0 && (
                <div className="instant-analyzer__recs">
                  {result.recommendations.map((r, i) => (
                    <p key={i} className="instant-analyzer__rec-item">{r}</p>
                  ))}
                </div>
              )}

              {/* Signup CTA */}
              <button
                type="button"
                onClick={handleSignup}
                className="home-marketing-cta instant-analyzer__signup-cta group font-sans touch-manipulation"
              >
                {hasJd
                  ? t('home.analyzer.ctaOptimize')
                  : t('home.analyzer.ctaImprove')}
                <ArrowRight className="h-4 w-4 shrink-0 transition-transform duration-200 group-hover:translate-x-0.5" aria-hidden />
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

/* ── Helpers ── */
function extractSkillsFromText(text: string): string[] {
  const techSkills = [
    'javascript', 'typescript', 'python', 'java', 'c#', 'c++', 'go', 'rust', 'ruby', 'php', 'swift', 'kotlin',
    'react', 'angular', 'vue', 'next.js', 'node.js', 'express', 'django', 'flask', 'spring', 'laravel',
    'aws', 'azure', 'gcp', 'docker', 'kubernetes', 'terraform', 'jenkins', 'ci/cd',
    'sql', 'nosql', 'mongodb', 'postgresql', 'mysql', 'redis', 'elasticsearch',
    'rest', 'graphql', 'microservices', 'api', 'html', 'css', 'sass', 'tailwind',
    'git', 'agile', 'scrum', 'jira', 'figma', 'webpack', 'vite',
    'machine learning', 'deep learning', 'nlp', 'computer vision', 'data science',
    'tensorflow', 'pytorch', 'pandas', 'numpy', 'spark', 'hadoop',
    'linux', 'networking', 'security', 'devops', 'sre',
    'product management', 'project management', 'leadership', 'communication',
  ];

  const lower = text.toLowerCase();
  return techSkills.filter((skill) => lower.includes(skill));
}

export default React.memo(HomeInstantAnalyzer);
